import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaHourglassHalf, FaSwimmingPool, FaBook, FaDumbbell, FaCoffee, FaHospital, FaWalking } from 'react-icons/fa';
import Seo from '../components/Seo';

function NewPoolUpdates() {
  const projectDetails = [
    { icon: FaSwimmingPool, title: '£20 Million Investment', text: "Stockport Council has secured significant funding through the government's Levelling Up Fund to deliver this flagship community facility for Marple." },
    { icon: FaHospital, title: 'Integrated Services', text: 'The hub will co-locate leisure, health and library services, including a new home for Marple Clinic, creating a vibrant community space.' },
    { icon: FaSwimmingPool, title: 'Permanent Home', text: 'After years without a local pool since Marple Baths closed in 2018, residents will finally have a modern swimming facility in their town again.' },
  ];

  const features = [
    { icon: FaSwimmingPool, text: 'Five-lane swimming pool — perfect for competitive training and learn-to-swim programs' },
    { icon: FaBook, text: 'New library facilities — modern community learning space' },
    { icon: FaDumbbell, text: '60-station gym — state-of-the-art fitness facilities' },
    { icon: FaCoffee, text: 'Café — community gathering space' },
    { icon: FaHospital, text: 'New home for Marple Clinic — integrated health services' },
    { icon: FaWalking, text: 'Enhanced walking and cycling access — improved connectivity to the site' },
  ];

  const milestones = [
    { done: true, date: 'April 2025', title: 'Groundbreaking Ceremony', text: 'Official groundbreaking ceremony held in Marple Memorial Park with local leaders and partners, marking the start of construction on the £20m project.' },
    { done: true, date: '2025', title: 'Construction Progress', text: 'Main contractor Willmott Dixon has been making steady progress throughout 2025, with regular monthly updates showing construction milestones being met.' },
    { done: true, date: 'June 2026', title: 'Brickwork Complete & Cladding Underway', text: 'The building’s brickwork is now complete with scaffolding removed on all sides, and cladding is being installed on the north, east and west elevations. Internal works — walls, ceilings and M&E installation — are progressing well.' },
    { done: true, date: 'September 2026', title: 'Clinic Demolition & Pool Screeding Complete', text: 'Demolition of the former clinic building is complete, screeding around the swimming pool perimeter is finished, and air source heat pumps are installed. Car park formation, landscaping and internal decoration works are starting next.' },
    { done: false, date: 'Autumn 2026', title: 'Opening', text: 'The new Marple Community Hub is scheduled to open in autumn 2026, bringing together leisure, health, and library services under one roof for the benefit of the entire community.' },
  ];

  const archiveUpdates = [
    { title: 'June 2026 Construction Progress', text: "The building's brickwork was completed, with scaffolding removed from all sides, and cladding installed on the north, east and west elevations. Internal works progressed too, including walls, ceilings and mechanical & electrical installation, alongside nearby highway and play area improvements.", link: 'https://www.stockport.gov.uk/marple-hub/june-2026-update', linkText: 'Read June 2026 Update', published: 'June 2026' },
    { title: 'January 2026 Construction Progress', text: 'The new Marple Community Hub is set to open later in 2026. Construction work has been progressing well since the groundbreaking ceremony in April 2025, and the £20 million project is on track to deliver state-of-the-art facilities for the community.', link: 'https://www.stockport.gov.uk/marple-community-hub/january-2026-update', linkText: 'Read January 2026 Update', published: 'January 2026' },
    { title: 'October 2025 Construction Progress', text: 'The latest monthly update from Stockport Council provides details on the current construction phase, upcoming milestones, and progress towards completion of the new Marple Community Hub.', link: 'https://www.stockport.gov.uk/marple-community-hub/october-2025-update-marple-community-hub', linkText: 'Read October 2025 Update', published: 'October 2025' },
    { title: 'May 2025 Construction Progress', text: 'Monthly update from Stockport Council detailing the construction phase, milestones, and progress towards completion of the new Marple Community Hub.', link: 'https://www.stockport.gov.uk/marple-community-hub/may-2025-update-marple-community-hub', linkText: 'Read May 2025 Update', published: 'May 2025' },
  ];

  return (
    <main className="py-16 bg-gradient-to-b from-gray-50 to-white">
      <Seo
        title="New Pool Updates"
        description="Latest updates on the new Marple Community Hub swimming pool, including progress, timelines and what it means for the club."
        path="/new-pool"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="font-display text-5xl font-bold text-navy-900 mb-6">New Pool Construction Updates</h1>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Stay up to date with the progress of our new Marple Community Hub. Stockport Council
            provides regular monthly updates on the construction progress, planning developments,
            and key milestones for this exciting £20 million project.
          </p>
        </motion.div>

        {/* Latest Update */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-display text-3xl font-bold text-navy-900 text-center mb-8">Latest Update &mdash; September 2026</h2>
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-md p-8 border-t-4 border-primary-600">
            <h3 className="text-xl font-bold text-primary-700 mb-4">September 2026 Construction Progress</h3>
            <p className="text-gray-700 mb-4 leading-relaxed">
              <strong>Work continues to progress well</strong> on the Marple Hub development, with a number of key
              milestones achieved over the past month as the project moves closer to completion. Demolition of the
              former clinic building has now been completed, new air source heat pumps have been installed within
              the plant room to support the hub's energy-efficient and sustainable design, and screeding works
              around the perimeter of the swimming pool have been completed. Construction of the swale drainage
              feature is also progressing, with fencing installed and reduced-level excavation works underway.
            </p>
            <p className="text-gray-700 mb-6 leading-relaxed">
              Over the next four weeks, the project team will be focusing on continuing construction of the swale
              drainage system, starting formation of the new car park, beginning both hard and soft landscaping
              works across the site, and starting internal decoration works within the building. The hub remains
              on track to open in <strong>autumn 2026</strong>.
            </p>
            <div className="bg-blue-50 border border-blue-300 rounded-xl p-6 mb-6">
              <h4 className="font-bold text-blue-800 mb-4">What's Inside the New Hub</h4>
              <ul className="space-y-3">
                {features.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <li key={i} className="flex items-start gap-3 text-gray-800">
                      <Icon className="text-blue-600 mt-1 flex-shrink-0" />
                      <span>{f.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <a
              href="https://www.stockport.gov.uk/marple-hub/september-2026-update"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary-600 text-white px-6 py-3 rounded-full font-bold hover:bg-primary-700 transition-all duration-300 shadow-md mb-4"
            >
              Read September 2026 Update
            </a>
            <p className="text-sm text-gray-500">
              <strong>Published:</strong> September 2026 &nbsp;|&nbsp; <strong>Source:</strong> Stockport Council
            </p>
          </div>
        </motion.section>

        {/* Masters/Adults Interest CTA */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="max-w-3xl mx-auto bg-gradient-to-br from-primary-50 to-blue-50 border-2 border-primary-500 rounded-2xl p-8 text-center shadow-md">
            <h3 className="font-display text-2xl font-bold text-navy-900 mb-3">Interested in Masters or Adult Swimming?</h3>
            <p className="text-gray-700 mb-6 max-w-xl mx-auto">
              With the move to the new pool, we're looking at introducing Masters and Adult swimming sessions.
              Register your interest so we can gauge demand and plan sessions around you.
            </p>
            <Link
              to="/masters-interest"
              className="inline-block bg-primary-600 text-white px-6 py-3 rounded-full font-bold hover:bg-primary-700 transition-all duration-300 shadow-md"
            >
              Register Your Interest
            </Link>
          </div>
        </motion.section>

        {/* Timeline */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-display text-3xl font-bold text-navy-900 text-center mb-10">Construction Timeline &amp; Milestones</h2>
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-md p-8">
            <div className="space-y-8 border-l-2 border-gray-200 pl-6">
              {milestones.map((m, i) => (
                <div key={i} className="relative">
                  <span className={`absolute -left-[31px] top-1 w-4 h-4 rounded-full border-4 border-white ${m.done ? 'bg-green-600' : 'bg-amber-500'}`} />
                  <div className="flex items-center gap-2 mb-1">
                    {m.done ? <FaCheckCircle className="text-green-600" /> : <FaHourglassHalf className="text-amber-500" />}
                    <h4 className="font-bold text-navy-900">{m.date} &mdash; {m.title}</h4>
                  </div>
                  <p className="text-gray-600">{m.text}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Project Overview Images */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-display text-3xl font-bold text-navy-900 text-center mb-10">Project Overview</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <img
                src="/new-pool-interior.jpg"
                alt="New Pool Interior Design"
                className="w-full rounded-2xl shadow-xl"
              />
              <h3 className="font-bold text-navy-900 mt-4 mb-2 text-lg">Swimming Pool Facilities</h3>
              <p className="text-gray-700">
                The new community hub will feature a modern five-lane swimming pool with contemporary
                design, perfect for our competitive training programs and learn-to-swim classes.
              </p>
            </div>
            <div>
              <img
                src="/new-pool-exterior.jpg"
                alt="New Community Hub Exterior"
                className="w-full rounded-2xl shadow-xl"
              />
              <h3 className="font-bold text-navy-900 mt-4 mb-2 text-lg">Community Hub Design</h3>
              <p className="text-gray-700">
                The building features contemporary architecture with extensive glazing,
                creating a bright and welcoming environment that will serve the Marple community for generations.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Key Project Details */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-display text-3xl font-bold text-navy-900 text-center mb-10">Key Project Details</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {projectDetails.map((d, i) => {
              const Icon = d.icon;
              return (
                <div key={i} className="bg-white p-6 rounded-2xl shadow-md text-center">
                  <Icon className="text-3xl text-primary-600 mx-auto mb-4" />
                  <h3 className="font-bold text-navy-900 mb-2">{d.title}</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">{d.text}</p>
                </div>
              );
            })}
          </div>
        </motion.section>

        {/* Previous Updates */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <h2 className="font-display text-3xl font-bold text-navy-900 text-center mb-10">Previous Updates</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {archiveUpdates.map((u, i) => (
              <div key={i} className="bg-white rounded-2xl shadow-md p-8 border-t-4 border-gray-300">
                <h3 className="text-xl font-bold text-navy-900 mb-3">{u.title}</h3>
                <p className="text-gray-700 mb-4 leading-relaxed">{u.text}</p>
                <a
                  href={u.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-navy-800 text-white px-5 py-2.5 rounded-full font-semibold hover:bg-navy-900 transition-all duration-300 mb-3"
                >
                  {u.linkText}
                </a>
                <p className="text-sm text-gray-500"><strong>Published:</strong> {u.published} &nbsp;|&nbsp; <strong>Source:</strong> Stockport Council</p>
              </div>
            ))}
            <div className="bg-gray-50 rounded-2xl p-8 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-700 mb-3">Archive of Construction Updates</h3>
              <p className="text-gray-600 mb-3">
                We continue to monitor and share monthly updates from Stockport Council to keep our members
                and the community informed about the construction progress as we approach the opening in 2026.
              </p>
              <p className="text-sm text-gray-500">
                Check back regularly for the latest news and developments on the new Marple Community Hub.
              </p>
            </div>
          </div>
        </motion.section>

        {/* Stay Informed */}
        <motion.section
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <h2 className="font-display text-3xl font-bold text-navy-900 text-center mb-10">Stay Informed</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-2xl shadow-md text-center">
              <h3 className="font-bold text-navy-900 mb-2">Opening Autumn 2026</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                The new Marple Community Hub is on track to open in autumn 2026, providing a permanent home
                for Marple Swimming Club and serving the community for generations to come.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-md text-center">
              <h3 className="font-bold text-navy-900 mb-2">Club Communications</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Important updates affecting club operations and transition plans to the new facility
                will be communicated directly to members through our usual channels.
              </p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-md text-center">
              <h3 className="font-bold text-navy-900 mb-2">Questions?</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                If you have questions about the new facility or how it will affect
                club programs, please contact us or speak to coaches at training sessions.
              </p>
            </div>
          </div>
        </motion.section>
      </div>
    </main>
  );
}

export default NewPoolUpdates;
